import React, { useEffect, useState } from "react";
import { Repeat, Users, X } from "react-feather";
import { useTheme } from "../../theme/ThemeContext";
import { initialSharing, sharingBody } from "../../tasks/sharing";
import { REPEAT_OPTIONS, repeatOptionLabel, localToday, repeatLabel, formatDue, dueText, taskBucket, primaryAction } from "../../tasks/taskUtils";
import { Card, SectionTitle, Sheet, Button, Field, Empty, useInputStyle } from "../ui";
import PhotoField from "../../tasks/PhotoField";

const roleWord = { admin: "Admin", doer: "Doer" };

const Badge = ({ children, tone = "neutral" }) => {
  const { tokens } = useTheme();
  const tones = {
    neutral: [tokens.neutralSoft, tokens.textMuted],
    accent: [tokens.accentSoft, tokens.accent],
    pending: [tokens.pendingBg, tokens.pending],
    positive: [tokens.positiveBg, tokens.positive],
  };
  const [bg, fg] = tones[tone];
  return <span style={{ padding: "3px 9px", borderRadius: 999, fontSize: 11, fontWeight: 700, backgroundColor: bg, color: fg }}>{children}</span>;
};

const eventText = (e) => {
  const who = e.username;
  if (e.kind === "submitted") return `${who} said it's done`;
  if (e.kind === "confirmed") return `${who} confirmed it`;
  if (e.kind === "done") return `${who} marked it done`;
  if (e.kind === "rejected") return `${who} sent it back`;
  return `${who}: ${e.kind}`;
};

// ---- add / edit -------------------------------------------------------------

const Segment = ({ options, value, onChange }) => {
  const { tokens } = useTheme();
  return (
    <div style={{ display: "flex", padding: 4, borderRadius: 12, backgroundColor: tokens.neutralSoft, marginBottom: 12 }}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          style={{ flex: 1, padding: "10px 8px", border: "none", borderRadius: 9, cursor: "pointer", fontSize: 14, fontWeight: 700, backgroundColor: value === o.value ? tokens.surface : "transparent", color: value === o.value ? tokens.textPrimary : tokens.textMuted, boxShadow: value === o.value ? tokens.cardShadow : "none" }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
};

export const TaskForm = ({ task, tasksApi, groupsApi, onClose }) => {
  const input = useInputStyle();
  const { tokens } = useTheme();
  const editing = !!task;

  const [form, setForm] = useState({
    title: task ? task.title : "",
    description: task ? task.description : "",
    repeatUnit: task ? task.repeatUnit : "once",
    repeatEvery: task ? String(task.repeatEvery) : "1",
    dueDate: task ? task.dueDate : localToday(),
    dueTime: task && task.dueTime ? task.dueTime : "",
  });
  const [sharing, setSharing] = useState(() => initialSharing(task));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // The task list only carries a `hasPhoto` flag (kept light); the actual image is
  // fetched only when opening the edit form, and only if there is one to fetch.
  const [photo, setPhoto] = useState(null);
  const [photoTouched, setPhotoTouched] = useState(false);
  useEffect(() => {
    if (task && task.hasPhoto) tasksApi.details(task.id).then((d) => setPhoto(d.photo)).catch(() => {});
  }, [task, tasksApi]);
  const changePhoto = (value) => {
    setPhoto(value);
    setPhotoTouched(true);
  };

  const available = groupsApi.contacts.filter((c) => !sharing.people.includes(c.username));

  // Picking a name adds them straight away; nothing else to tap.
  const addPeople = (usernames) => {
    setError("");
    setSharing((s) => ({ ...s, people: [...s.people, ...usernames.filter((u) => !s.people.includes(u))] }));
  };
  const removePerson = (username) =>
    setSharing((s) => ({ ...s, people: s.people.filter((p) => p !== username), adminWho: s.adminWho === username ? "me" : s.adminWho }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return setError("Give the task a name.");
    if (sharing.audience === "others" && sharing.people.length === 0) return setError("Choose who this task is for.");
    setSaving(true);
    setError("");
    const body = {
      title: form.title,
      description: form.description,
      repeatUnit: form.repeatUnit,
      repeatEvery: form.repeatUnit === "once" ? 1 : parseInt(form.repeatEvery, 10) || 1,
      dueDate: form.dueDate,
      dueTime: form.dueTime || null,
      ...sharingBody(sharing),
    };
    // Only sent when the user actually touched the photo field, so an unrelated
    // edit (renaming, changing the date) never re-uploads the same image.
    if (!editing || photoTouched) body.photo = photo;
    try {
      if (editing) await tasksApi.update(task.id, body);
      else await tasksApi.create(body);
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <Sheet title={editing ? "Edit task" : "New task"} onClose={onClose}>
      <form onSubmit={submit}>
        <Field label="Task name"><input style={input} value={form.title} onChange={set("title")} placeholder="e.g. Pay electricity bill" maxLength={100} /></Field>
        <Field label="Details (optional)"><textarea style={{ ...input, minHeight: 70, resize: "vertical" }} value={form.description} onChange={set("description")} placeholder="Anything you need to remember" /></Field>
        <PhotoField tokens={tokens} label="Photo (optional) — shows exactly what needs doing" value={photo} onChange={changePhoto} />

        <div style={{ display: "flex", gap: 10 }}>
          {/* minWidth: 0 stops a native time input (wide on iOS) from pushing past its share of the row */}
          <div style={{ flex: 1, minWidth: 0 }}><Field label="Date"><input style={input} type="date" value={form.dueDate} onChange={set("dueDate")} /></Field></div>
          <div style={{ flex: 1, minWidth: 0 }}><Field label="Time (optional)"><input style={input} type="time" value={form.dueTime} onChange={set("dueTime")} /></Field></div>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <Field label="Repeats">
              <select style={input} value={form.repeatUnit} onChange={set("repeatUnit")}>
                {REPEAT_OPTIONS.map((o) => <option key={o.unit} value={o.unit}>{repeatOptionLabel(o)}</option>)}
              </select>
            </Field>
          </div>
          {form.repeatUnit !== "once" && (
            <div style={{ width: 90 }}>
              <Field label="Every"><input style={input} type="number" min="1" max="365" inputMode="numeric" value={form.repeatEvery} onChange={set("repeatEvery")} /></Field>
            </div>
          )}
        </div>

        <div style={{ margin: "6px 0 8px", fontSize: 13, fontWeight: 700, color: tokens.textPrimary }}>Who is this for?</div>
        <Segment
          value={sharing.audience}
          onChange={(audience) => setSharing((s) => ({ ...s, audience }))}
          options={[{ value: "me", label: "Just me" }, { value: "others", label: "Other people" }]}
        />

        {sharing.audience === "others" && (
          <>
            {groupsApi.contacts.length === 0 ? (
              <p style={{ margin: "0 0 14px", fontSize: 13, color: tokens.textMuted }}>
                To share a task, first add people to a group under You → Groups.
              </p>
            ) : (
              <>
                {sharing.people.length > 0 && (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                    {sharing.people.map((u) => (
                      <span key={u} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 6px 6px 12px", borderRadius: 999, fontSize: 14, fontWeight: 600, backgroundColor: tokens.accentSoft, color: tokens.textPrimary }}>
                        {u}
                        <button type="button" onClick={() => removePerson(u)} aria-label={`Remove ${u}`} style={{ display: "flex", padding: 3, border: "none", borderRadius: 999, background: "none", cursor: "pointer", color: tokens.textMuted }}>
                          <X size={16} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {available.length > 0 && (
                  <select style={{ ...input, marginBottom: 8 }} value="" onChange={(e) => e.target.value && addPeople([e.target.value])}>
                    <option value="">{sharing.people.length ? "Add another person…" : "Choose a person…"}</option>
                    {available.map((c) => <option key={c.userId} value={c.username}>{c.username}</option>)}
                  </select>
                )}

                {groupsApi.groups.filter((g) => g.members.length > 0).length > 0 && available.length > 0 && (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                    {groupsApi.groups.filter((g) => g.members.length > 0).map((g) => (
                      <button key={g.id} type="button" onClick={() => addPeople(g.members.map((m) => m.username))} style={{ padding: "6px 12px", borderRadius: 999, border: `1px solid ${tokens.surfaceBorder}`, background: "none", cursor: "pointer", fontSize: 12, fontWeight: 600, color: tokens.textMuted }}>
                        + Everyone in {g.name}
                      </button>
                    ))}
                  </div>
                )}

                {sharing.people.length > 0 && (
                  <>
                    <Field label="Who confirms it's done?">
                      <select style={input} value={sharing.adminWho} onChange={(e) => setSharing((s) => ({ ...s, adminWho: e.target.value }))}>
                        <option value="me">Me</option>
                        {sharing.people.map((u) => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </Field>
                    <p style={{ margin: "0 0 14px", fontSize: 12, color: tokens.textMuted }}>
                      Everyone else just taps "I've done it", and then waits for the confirmation.
                    </p>
                  </>
                )}
              </>
            )}
          </>
        )}

        {error && <div role="alert" style={{ marginBottom: 10, fontSize: 13, color: tokens.negative }}>{error}</div>}
        <Button type="submit" disabled={saving} style={{ width: "100%" }}>{saving ? "Saving…" : editing ? "Save changes" : "Add task"}</Button>
      </form>
    </Sheet>
  );
};
// ---- mark as done (optional proof photo + note) --------------------------------

const SubmitSheet = ({ task, tasksApi, onClose, onDone }) => {
  const { tokens } = useTheme();
  const input = useInputStyle();
  const [photo, setPhoto] = useState(null);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const send = async () => {
    setSaving(true);
    setError("");
    try {
      await tasksApi.submit(task.id, note.trim(), photo);
      onDone();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <Sheet title="Mark as done" onClose={onClose}>
      {!!task.description && <p style={{ margin: "0 0 14px", fontSize: 13, color: tokens.textMuted }}>{task.description}</p>}
      <PhotoField tokens={tokens} label="Photo (optional) — show it's done" value={photo} onChange={setPhoto} />
      <Field label="Note (optional)"><input style={input} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything the admin should know" /></Field>
      {error && <div role="alert" style={{ marginBottom: 10, fontSize: 13, color: tokens.negative }}>{error}</div>}
      <Button disabled={saving} onClick={send} style={{ width: "100%" }}>{saving ? "Sending…" : "I've done it"}</Button>
    </Sheet>
  );
};

// ---- detail -------------------------------------------------------------------

const TaskDetail = ({ taskId, tasksApi, onClose, onEdit }) => {
  const { tokens } = useTheme();
  const task = tasksApi.tasks.find((t) => t.id === taskId);
  const [detail, setDetail] = useState(null); // full GET /tasks/:id - has the actual photo + history
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const stamp = task ? `${task.state}|${task.dueDate}|${task.hasPhoto}` : "";
  useEffect(() => {
    tasksApi.details(taskId).then(setDetail).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, stamp]);

  if (!task) return null;
  const action = primaryAction(task);
  const history = detail ? detail.history : [];

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (err) {
      setError(err.message);
    }
    setBusy(false);
  };

  if (submitting) {
    return <SubmitSheet task={task} tasksApi={tasksApi} onClose={() => setSubmitting(false)} onDone={onClose} />;
  }

  return (
    <Sheet title={task.title} onClose={onClose}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <Badge tone="accent">{repeatLabel(task)}</Badge>
        <Badge tone={task.dueDate < localToday() && task.state !== "completed" ? "pending" : "neutral"}>{formatDue(task)}</Badge>
        {task.myRole && <Badge>{`You: ${roleWord[task.myRole]}`}</Badge>}
        {task.state === "submitted" && <Badge tone="pending">Waiting for confirmation</Badge>}
        {task.state === "completed" && <Badge tone="positive">Completed</Badge>}
      </div>
      {!!task.description && <p style={{ margin: "0 0 14px", fontSize: 14, lineHeight: 1.5, color: tokens.textPrimary }}>{task.description}</p>}

      {detail && detail.photo && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ marginBottom: 6, fontSize: 12, fontWeight: 600, color: tokens.textMuted }}>What needs doing</div>
          <img src={detail.photo} alt="" style={{ width: "100%", maxHeight: 260, objectFit: "cover", borderRadius: 12, border: `1px solid ${tokens.surfaceBorder}` }} />
        </div>
      )}

      {task.members.length > 1 && (
        <div style={{ marginBottom: 14, fontSize: 13, color: tokens.textMuted }}>
          {task.members.map((m) => `${m.username} (${roleWord[m.role]})`).join(" · ")}
        </div>
      )}

      {task.lastEvent && task.lastEvent.kind === "rejected" && task.state === "open" && (
        <div style={{ marginBottom: 14, padding: 12, borderRadius: 12, fontSize: 13, backgroundColor: tokens.pendingBg, color: tokens.pending }}>
          Sent back by {task.lastEvent.username}{task.lastEvent.note ? `: "${task.lastEvent.note}"` : "."}
        </div>
      )}

      {error && <div role="alert" style={{ marginBottom: 10, fontSize: 13, color: tokens.negative }}>{error}</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {action && (
          <Button disabled={busy} onClick={() => (action.kind === "submit" ? setSubmitting(true) : run(() => tasksApi.confirm(task.id)))}>
            {action.label}
          </Button>
        )}
        {task.myRole === "admin" && task.state === "submitted" && (
          <Button kind="ghost" disabled={busy} onClick={() => run(() => tasksApi.reject(task.id))}>Not done yet, send back</Button>
        )}
        {task.state === "submitted" && task.myRole === "doer" && (
          <p style={{ margin: 0, fontSize: 13, color: tokens.textMuted, textAlign: "center" }}>You've said it's done. An admin needs to confirm.</p>
        )}
        {task.isOwner && <Button kind="ghost" onClick={() => onEdit(task)}>Edit task</Button>}
        {/* Someone else's task is only taken off my list; the others keep it. */}
        {!confirmDelete && <Button kind="danger" onClick={() => setConfirmDelete(true)}>Delete task</Button>}
        {confirmDelete && (
          <Button kind="danger" disabled={busy} onClick={() => run(async () => { await tasksApi.remove(task.id); onClose(); })}>
            {task.isOwner ? "Tap again to delete for everyone" : `Tap again to remove it from your tasks (${task.ownerName} keeps it)`}
          </Button>
        )}
      </div>

      {history.length > 0 && (
        <>
          <div style={{ margin: "22px 0 8px", fontSize: 13, fontWeight: 700, color: tokens.textPrimary }}>History</div>
          {history.map((e, i) => (
            <div key={i} style={{ padding: "8px 0", borderTop: `1px solid ${tokens.surfaceBorder}`, fontSize: 13, color: tokens.textMuted }}>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                {e.photo && <img src={e.photo} alt="" style={{ width: 44, height: 44, borderRadius: 8, objectFit: "cover", flexShrink: 0, border: `1px solid ${tokens.surfaceBorder}` }} />}
                <div>
                  <div style={{ color: tokens.textPrimary }}>{eventText(e)}</div>
                  <div>for {e.occurrenceDate}{e.note ? ` · "${e.note}"` : ""}</div>
                </div>
              </div>
            </div>
          ))}
        </>
      )}
    </Sheet>
  );
};

// ---- list ---------------------------------------------------------------------

const TaskCard = ({ task, onOpen }) => {
  const { tokens } = useTheme();
  const bucket = taskBucket(task);
  const overdue = task.state === "open" && task.dueDate < localToday();
  const shared = task.members.length > 1;

  return (
    <Card style={{ padding: 0 }}>
      <button onClick={onOpen} style={{ width: "100%", padding: 16, background: "none", border: "none", textAlign: "left", cursor: "pointer", color: tokens.textPrimary }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
          <div style={{ fontSize: 15, fontWeight: 700, minWidth: 0 }}>{task.title}</div>
          {bucket === "confirm" && <Badge tone="pending">Confirm</Badge>}
          {bucket === "waiting" && <Badge tone="pending">Waiting</Badge>}
          {bucket === "done" && <Badge tone="positive">Done</Badge>}
        </div>
        {!!task.description && (
          <div style={{ marginTop: 3, fontSize: 13, color: tokens.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{task.description}</div>
        )}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 8, fontSize: 12, color: overdue ? tokens.negative : tokens.textMuted, fontWeight: overdue ? 700 : 400 }}>
          <span>{dueText(task)} · {formatDue(task)}</span>
          {task.repeatUnit !== "once" && <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Repeat size={12} /> {repeatLabel(task)}</span>}
          {shared && <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Users size={12} /> {task.members.length}</span>}
        </div>
      </button>
    </Card>
  );
};

const TasksScreen = ({ tasksApi, groupsApi }) => {
  const [openId, setOpenId] = useState(null);
  const [form, setForm] = useState(null); // null = closed, {} = new, task = editing

  const buckets = { action: [], later: [], others: [], done: [] };
  for (const t of tasksApi.tasks) {
    const b = taskBucket(t);
    if (b === "todo" || b === "confirm") buckets.action.push(t);
    else if (b === "later") buckets.later.push(t);
    else if (b === "done") buckets.done.push(t);
    else buckets.others.push(t); // waiting on others, or someone else is the doer
  }

  const list = (items) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {items.map((t) => <TaskCard key={t.id} task={t} onOpen={() => setOpenId(t.id)} />)}
    </div>
  );

  return (
    <div>
      {tasksApi.tasks.length === 0 && !tasksApi.loading && (
        <Empty>No tasks yet. Tap + to add a reminder. It can repeat, and you can share it with someone.</Empty>
      )}

      {buckets.action.length > 0 && <><SectionTitle>Needs you now</SectionTitle>{list(buckets.action)}</>}
      {buckets.later.length > 0 && <><SectionTitle>Coming up</SectionTitle>{list(buckets.later)}</>}
      {buckets.others.length > 0 && <><SectionTitle>With others</SectionTitle>{list(buckets.others)}</>}
      {buckets.done.length > 0 && <><SectionTitle>Completed</SectionTitle>{list(buckets.done)}</>}

      {openId && !form && <TaskDetail taskId={openId} tasksApi={tasksApi} onClose={() => setOpenId(null)} onEdit={(t) => setForm(t)} />}
      {form && <TaskForm task={form.id ? form : null} tasksApi={tasksApi} groupsApi={groupsApi} onClose={() => setForm(null)} />}
    </div>
  );
};

export default TasksScreen;
